import React, { useState } from "react";
import { observer } from "mobx-react";
import { localizeNumber, toNumber, triggerAnalystCommentModal, setUiNumInputErrorState, getUiNumInputErrorState, getCommentIconRenderingInfo, getInputRenderingInfo, errorExplanationOptions } from '../helpers'
import store from './Stores/SurveyStore';
import { saveValueNew, openPrevYearPanel } from '../control'
import {
    Tooltip,
} from 'react-tippy';
import { InputState, NumberFormat } from "../Enums/constants";
import { getSpecialIBSNumInputs, handleSpecialNumInputs, handleSpecialNumOnChange, checkSpecialSaveConfigs } from "../ibsHelper";
import { getSurveyResponseValue, setSurveyResponseValue } from "../Helpers/General/SurveyResponsesHelper";
import { formatNumber } from "../Helpers/General/FormatHelper";

const classNames = require('classnames');

const SurveyNumberInput = observer((props) => {

    const [isFocused, setisFocused] = useState(false);
    const [focusedValue, setfocusedValue] = useState("");
    const [initialFocus, setinitialFocus] = useState(false);
    const [originalValue, setOriginalValue] = useState(null);

    /**
     * Gets the value from the Mobx store
     * @returns {number|*}
     */
    const getStoreValue = () => {
        const valueFromStore = getSurveyResponseValue(props.name, props.rowData, props)
        const normalizedValue = normalizeUkPercentageForDisplay(valueFromStore)
        return normalizedValue;
    }

    /**
     * Updates the value in the mobx store
     * @param value newValue to be put into the store
     */
    const updateStoreValue = (value) => {
        value = normalizeUkPercentageForDatabase(value)
        setSurveyResponseValue(props.name, value, props)
    }

    /**
     * Updates the values inside the mobx store, and then saves the value to the backend through API
     * @param value the value to be saved
     * @param inputStateId this id determines whether the value is normal, Na, or Unknown
     */
    const updateStoreValueAndDatabaseValue = (value, inputStateId = InputState.NOT_SET) => {
        value = normalizeUkPercentageForDatabase(value)
        setSurveyResponseValue(props.name, value, props)

        const ibsExtraProps = checkSpecialSaveConfigs(props.participantType)
        saveValueNew(props.name, value, {
            inputStateId: inputStateId,
            ...ibsExtraProps
        });
    }
    
    const validate = val => {
        //blank values are valid
        if (val === "") return true;
        //validate the input to ensure numerical or unknown value
        if (props.allowNA && store.local.naList.includes(val)
            || props.allowUnknown && store.local.unknownList.includes(val)
            || props.allowNoTarget && store.local.noTargetList.includes(val.toLowerCase())) {
            return true;
        }
        
        /* Can assume val is a number from this point forward */
        
        const valAsNumber = toNumber(val);
        return !Number.isNaN(valAsNumber);
    };

    const onChange = e => {
        let newValue = e.target.value;
        handleSpecialNumOnChange(props, newValue)
        setinitialFocus(false);

        // if it's a percentage field 
        if(props.format === NumberFormat.PERCENTAGE ||
            props.format === NumberFormat.PERCENTAGE_UK ||
            props.format === NumberFormat.PERCENTAGEINTEGER) {
            newValue = newValue.replace('%', '').trim(); // remove any percentage sign
        }

        // Cleanup value if number
        if (!isNaN(newValue)) {
            newValue = newValue
                .trim()
                .replace(/(?!^)-/g, "") // remove all minus sign except the start
                .replace(/(\..*)\.$/, "$1") // If a number ends with a decimal dot (e.g., "5.3."), remove the final dot.
                .replace(/\.(?=.*\.)/g, "") // Remove all dots except the first one
        }

        updateStoreValue(newValue)
    };

    const onFocus = e => {
        setisFocused(true);
        setinitialFocus(true);
        let value = getStoreValue()
        setfocusedValue(value);
        e.target.value = localizeNumber(value)
        setOriginalValue(value);
        openPrevYearPanel(props.name);
    };

    const onBlur = e => {
        setisFocused(false);
        setinitialFocus(false);
        let rawValue = e.target.value;

        let isValid = validate(rawValue);
        setUiNumInputErrorState(props.name, isValid, props.participantType)

        if (!isValid) {
            e.target.value = getResponseValueFormatted();
            store.previousYearValue = "";
            return;
        }

        rawValue = handleSpecialNumInputs(props.name, rawValue, props)
        if (focusedValue === rawValue) {
            e.target.value = getResponseValueFormatted();
            return;
        }

        let currentValue = toNumber(rawValue)
        if (Number.isNaN(currentValue)) {
            rawValue = rawValue.toUpperCase()
            if (store.local.naList.includes(rawValue)) {
                updateStoreValueAndDatabaseValue("n/a", InputState.NA)
            } else if (store.local.unknownList.includes(rawValue)){
                updateStoreValueAndDatabaseValue("Unknown", InputState.UNKNOWN)
            } else if (store.local.noTargetList.includes(rawValue)) {
                updateStoreValueAndDatabaseValue("No target")
            } else if (rawValue === "") {
                updateStoreValueAndDatabaseValue(null)
            }
        } else {
            const isDirty = currentValue !== originalValue;
            if (isDirty) {
                updateStoreValueAndDatabaseValue(currentValue)
            }
        }
        
        e.target.value = getResponseValueFormatted();
        store.previousYearValue = ""
    };

    /**
     * UK Surveys saves percentage as a value between 0-1, but the UI takes value between 0-100.
     * Multiplies the value by 100, to make it suitable for displaying to user
     * @param value
     * @returns {number|*}
     */
    const normalizeUkPercentageForDisplay = (value) => {
        // If not percentage_uk, do nothing
        if (props.format !== NumberFormat.PERCENTAGE_UK)
            return value;

        // If the value is possibly allowed but not a valid number
        if (value === undefined ||
            value === null ||
            value === "" ||
            isNaN(value) ||
            value.toString().slice(-1) === "." ||
            store.local.naList.includes(value) ||
            store.local.unknownList.includes(value))
            return value;

        // This is complex because of some dumb JavaScript math float garbage
        const decimals = countDecimals(value);
        const precision = decimals - 2;
        const safePrecision = precision > 0 ? precision : 0;
        const scaled = Number(value) * 100;
        return Number(scaled.toFixed(safePrecision));
    }

    /**
     * UK Surveys saves percentage as a value between 0-1, but the UI takes value between 0-100.
     * Divides the value by 100, to make it suitable for saving to database
     * @param value
     * @returns {number|*}
     */
    const normalizeUkPercentageForDatabase = (value) => {
        // If not percentage_uk, do nothing
        if (props.format !== NumberFormat.PERCENTAGE_UK)
            return value;

        // If the value is possibly allowed but not a valid number
        if (value === undefined ||
            value === null ||
            value === "" ||
            isNaN(value) ||
            value.toString().slice(-1) === "." ||
            store.local.naList.includes(value) ||
            store.local.unknownList.includes(value))
            return value;

        // Fixes float garbage by maintaining precision relative to ×100 input
        const decimals = countDecimals(value);
        const precision = decimals + 2;
        const scaled = Number(value) * 0.01;
        return Number(scaled.toFixed(precision));
    }
    
    const getResponseValueFormatted = () => {
        if (store.questionMode) {
            return props.name;
        }
        
        let currentValue = getStoreValue()
        currentValue = getSpecialIBSNumInputs(props, currentValue)

        if (initialFocus) {
            return localizeNumber(currentValue);
        }

        if (isFocused) {
            // Currently in focus means edit mode
            return currentValue;
        } else if (isNaN(currentValue) || Number.isNaN(currentValue)) {
            // Not a number, return as is
            return currentValue;
        } else {
            if (Math.sign(currentValue) === -1) {
                return ("(" + formatNumber(props.format, Math.abs(currentValue)) + ")");
            } else {
                return formatNumber(props.format, currentValue);
            }
        }
    };

    let commentRenderingInfo = getCommentIconRenderingInfo(props.name)
    let inputRenderingInfo = getInputRenderingInfo(props.name)

    const getPlaceholder = () => {
        let placeholder = "#";
        if (props.format == NumberFormat.PERCENTAGE) {
            placeholder = "%"
        }
        if (props.placeholder) {
            placeholder = props.placeholder;
        }
        return placeholder;
    }
    
    const countDecimals = (value) => {
        if (value === null || value === undefined || value === "" || isNaN(value)) return 0;

        const str = Number(value).toString();
        const parts = str.split(".");
        if (parts.length < 2) return 0;

        return parts[1].replace(/0+$/, "").length;
    };
    
    const isOptions = () => {
        if (props.isOptions) return "optionsDD"
        else return ""
    }

    const isWidePlanInfo = () => {
        if (props.isWidePlanInfo) return " widePlanInfo"
        else return ""
    }

    const overrideAlignment = () => {
        if (props.overrideAlignment) return `${props.overrideAlignment}`;
        else return ``;
    }

    const renderNumInput = () => {
        return (
            <input onChange={onChange} 
                   onBlur={onBlur} 
                   onFocus={onFocus}
                   autoComplete="off"
                   placeholder={getPlaceholder()} 
                   disabled={inputRenderingInfo.isDisabled} 
                className={classNames(getUiNumInputErrorState(props.name, props.participantType), inputRenderingInfo.textClassName, inputRenderingInfo.inputColor, "numInput", props.inputStyleName, isOptions(), isWidePlanInfo(), overrideAlignment())}
                   type="text" name={props.name} value={getResponseValueFormatted()} />
        );
    }

    const renderTooltipText = () => {
        if (getUiNumInputErrorState(props.name, props.participantType) === "invalidInput") {
            return (<div className="genericTooltip">
                <span>Invalid Format. Please enter a number.</span>
            </div>)
        } 
        return (
            <div className="genericTooltip">
                <span dangerouslySetInnerHTML={{ __html: inputRenderingInfo.feedbackComment }}></span>
                <br />
                {errorExplanationOptions(props.name, inputRenderingInfo, getResponseValueFormatted())}
            </div>
        );
    }
    return (
        <div className="numinputDiv">
            {((inputRenderingInfo && inputRenderingInfo.feedbackComment) || getUiNumInputErrorState(props.name, props.participantType)=="invalidInput") &&
                <Tooltip
                    disabled={store.questionMode}
                    position="top"
                    trigger="mouseenter"
                    animation="fade"
                    arrow="true"
                    size="small"
                    interactive="true"
                    hideOnClick="false"
                    delay={250}
                    html={renderTooltipText()}
            >
                {renderNumInput()}
                </Tooltip>
            }

            {inputRenderingInfo === undefined || (!inputRenderingInfo.feedbackComment && getUiNumInputErrorState(props.name, props.participantType) != "invalidInput")&&
                renderNumInput()
            }
            &nbsp;
             {store.analystMode &&
                <a title={commentRenderingInfo.hoverComment}>&nbsp;&nbsp;&nbsp;<i className={"fa fa-comment " + commentRenderingInfo.className} onClick={() => triggerAnalystCommentModal(props.name)} style={{ visibility: store.analystMode || (store.accountInfo && store.accountInfo.isAnalyst && commentRenderingInfo.className) ? "" : "hidden" }}></i></a>
            }

           
        </div>
    );

});
export default SurveyNumberInput;

//try to make input only once and return it since theyre the same ^^